import { motion } from "framer-motion";
import {
  Instagram,
  Facebook,
  Youtube,
  Star,
  ArrowUpRight,
  ArrowLeft,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PRACTICE } from "../lib/data";
import { WhatsAppFab } from "../components/site/WhatsAppFab";

const DP_URL =
  "https://customer-assets-wrfwihn1.emergentagent.net/job_urology-landing/artifacts/njd4svzz_image.png";

const SOCIALS = [
  {
    key: "instagram",
    name: "Instagram",
    handle: "@drvenkatsubramaniam",
    description:
      "Surgical insights, patient education & behind-the-scenes from the OR. Follow for weekly reels.",
    cta: "Follow on Instagram",
    url: "https://www.instagram.com/drvenkatsubramaniam/",
    gradient: "linear-gradient(135deg, #FEDA75 0%, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5 100%)",
    accent: "#D62976",
    Icon: Instagram,
  },
  {
    key: "facebook",
    name: "Facebook",
    handle: "@drvenkatsubramaniam",
    description:
      "Longer updates, patient stories and health awareness posts. Like the page to stay informed.",
    cta: "Like on Facebook",
    url: "https://www.facebook.com/drvenkatsubramaniam/",
    gradient: "linear-gradient(135deg, #1877F2 0%, #0C5BC2 100%)",
    accent: "#1877F2",
    Icon: Facebook,
  },
  {
    key: "youtube",
    name: "YouTube",
    handle: "@DrVenkatsubramaniam",
    description:
      "Full-length educational videos — procedures explained, recovery tips, patient testimonials.",
    cta: "Subscribe on YouTube",
    url: "https://www.youtube.com/@DrVenkatsubramaniam",
    gradient: "linear-gradient(135deg, #FF0000 0%, #CC0000 100%)",
    accent: "#FF0000",
    Icon: Youtube,
  },
  {
    key: "google",
    name: "Google Business",
    handle: "4.9★ · 617+ reviews",
    description:
      "If Dr. Venkat has cared for you, a kind word on Google helps other patients find him.",
    cta: "Leave a Google Review",
    url: "https://share.google/VQqc1ZCSv4JihHDF6",
    gradient: "linear-gradient(135deg, #4285F4 0%, #34A853 33%, #FBBC05 66%, #EA4335 100%)",
    accent: "#4285F4",
    Icon: Star,
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function SocialPage() {
  return (
    <main className="min-h-screen bg-bone text-ink relative overflow-hidden">
      {/* Decorative background */}
      <div className="gradient-mesh" aria-hidden="true" />
      <div className="mesh-blob-3" aria-hidden="true" />

      <div className="relative max-w-2xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
        {/* Back link */}
        <Link
          to="/"
          data-testid="social-back-link"
          className="inline-flex items-center gap-2 text-sm font-medium text-ink-soft hover:text-ink transition mb-8"
        >
          <ArrowLeft size={16} /> Back to home
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-10"
        >
          <div className="relative inline-block mb-5">
            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden bg-paper border-4 border-paper shadow-[0_20px_40px_-15px_rgba(14,156,169,0.35)] ring-2 ring-forest/20">
              <img
                src={DP_URL}
                alt="Dr. Venkatsubramaniam"
                className="w-full h-full object-cover"
                style={{ objectPosition: "center 12%" }}
                data-testid="social-dp"
                draggable="false"
              />
            </div>
            <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-forest border-4 border-bone flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-white" />
            </span>
          </div>
          <h1 className="font-sans font-extrabold text-3xl sm:text-4xl tracking-tight text-ink">
            Dr. Venkatsubramaniam
          </h1>
          <p className="mt-2 text-sm sm:text-base text-ink-soft font-medium">
            Senior Urologist · Chennai
          </p>
          <p className="mt-1 text-xs tracking-[0.18em] uppercase font-mono text-forest font-semibold">
            M.S · D.Lap · M.Ch (Uro)
          </p>
          <p className="mt-6 text-base text-ink-soft max-w-md mx-auto">
            Follow Dr. Venkat across platforms — surgical insights, patient education, and updates on urological care.
          </p>
        </motion.div>

        {/* Social cards */}
        <div className="space-y-4">
          {SOCIALS.map((s, i) => (
            <motion.a
              key={s.key}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`social-link-${s.key}`}
              custom={i}
              initial="hidden"
              animate="show"
              variants={fadeUp}
              className="group block relative rounded-2xl bg-paper border border-line p-5 sm:p-6 overflow-hidden transition-all hover:border-forest hover:shadow-[0_20px_40px_-24px_rgba(14,156,169,0.35)] hover:-translate-y-1"
            >
              {/* Accent gradient strip on left edge */}
              <span
                aria-hidden="true"
                className="absolute left-0 top-0 bottom-0 w-1"
                style={{ background: s.gradient }}
              />

              <div className="flex items-center gap-4 sm:gap-5 pl-3">
                {/* Icon tile */}
                <div
                  className="shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-white shadow-md transition-transform group-hover:scale-105"
                  style={{ background: s.gradient }}
                >
                  <s.Icon size={28} strokeWidth={1.8} fill={s.key === "google" ? "currentColor" : "none"} />
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <h3 className="font-sans font-bold text-base sm:text-lg text-ink">
                      {s.name}
                    </h3>
                    <span className="text-xs sm:text-sm font-mono text-ink-soft truncate">
                      {s.handle}
                    </span>
                  </div>
                  <p className="mt-1 text-xs sm:text-sm text-ink-soft leading-snug line-clamp-2">
                    {s.description}
                  </p>
                  <span
                    className="mt-2 inline-flex items-center gap-1 text-xs font-semibold"
                    style={{ color: s.accent }}
                  >
                    {s.cta}
                    <ArrowUpRight size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Secondary actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          className="mt-10 bg-paper border border-line rounded-2xl p-5 sm:p-6 text-center"
        >
          <p className="eyebrow mb-3">Book Directly</p>
          <h3 className="font-sans font-bold text-xl text-ink">
            Need a consultation?
          </h3>
          <p className="mt-2 text-sm text-ink-soft">
            Reach the practice directly — the team responds within a few hours.
          </p>
          <div className="mt-5 flex flex-wrap gap-3 justify-center">
            <a
              href={`tel:${PRACTICE.phone.replace(/\s/g, "")}`}
              data-testid="social-call-btn"
              className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold"
            >
              Call {PRACTICE.phone}
            </a>
            <a
              href={`https://wa.me/${PRACTICE.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-wa-btn"
              className="btn-outline inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold bg-paper"
            >
              WhatsApp Team
            </a>
          </div>
        </motion.div>

        {/* Footer line */}
        <p className="text-center text-xs text-ink-soft/70 mt-10">
          © {new Date().getFullYear()} Dr. Venkatsubramaniam · Private Urological Practice
        </p>
      </div>

      <WhatsAppFab />
    </main>
  );
}
